/**
 * Error Boundary Component
 * Catches and displays errors in child components gracefully
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { logger } from '../../utils/logger';
import { designTokens } from '../../constants/designTokens';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught an error:', error);

    this.setState({
      errorInfo,
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      // Default error UI - using basic HTML/CSS to avoid context dependencies
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '24px',
            backgroundColor: designTokens.colors.background.primary,
            color: designTokens.colors.text.primary,
          }}
        >
          <div
            style={{
              maxWidth: '400px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '48px',
                marginBottom: '24px',
              }}
            >
              ⚠️
            </div>

            <h1
              style={{
                fontSize: '24px',
                fontWeight: 'bold',
                marginBottom: '16px',
              }}
            >
              Something went wrong
            </h1>

            <p
              style={{
                fontSize: '16px',
                color: designTokens.colors.text.secondary,
                marginBottom: '32px',
                lineHeight: '1.5',
              }}
            >
              We encountered an unexpected error. Please try again or contact
              support if the problem persists.
            </p>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && (
              <details
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: designTokens.colors.background.secondary,
                  borderRadius: '8px',
                  fontSize: '14px',
                  marginBottom: '24px',
                }}
              >
                <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
                  Error Details
                </summary>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: 0,
                    color: designTokens.colors.text.primary,
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo && (
                    <>
                      {'\n\nComponent Stack:\n'}
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </pre>
              </details>
            )}

            <button
              onClick={this.handleReset}
              style={{
                width: '100%',
                padding: '12px 24px',
                backgroundColor: designTokens.colors.brand.primary,
                color: designTokens.colors.text.inverse,
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook for functional components to reset error boundary
export function useErrorBoundary() {
  return {
    resetError: () => {
      // This would need to be connected to the nearest ErrorBoundary
      // For now, we'll just reload the page as a fallback
      window.location.reload();
    },
  };
}
